import React, { useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const parseJSONData = (val) => {
  if (!val) return null;
  if (typeof val === "object") return val;
  try {
    return JSON.parse(val);
  } catch (e) {
    return val;
  }
};

const formatValue = (val) => {
  if (val === null || val === undefined || val === "") {
    return <span className="text-muted font-monospace small">— (Empty)</span>;
  }
  if (typeof val === "boolean") {
    return (
      <span className={`badge ${val ? "bg-label-success" : "bg-label-secondary"}`}>
        {val ? "TRUE" : "FALSE"}
      </span>
    );
  }

  // If string contains stringified JSON object or array, try parsing it
  if (typeof val === "string" && (val.startsWith("{") || val.startsWith("["))) {
    const parsed = parseJSONData(val);
    if (parsed && typeof parsed === "object") {
      val = parsed;
    }
  }

  if (Array.isArray(val)) {
    if (val.length === 0) return <span className="text-muted font-monospace small">Empty Array []</span>;
    return (
      <div className="d-flex flex-wrap gap-1 mt-1" style={{ maxHeight: "160px", overflowY: "auto" }}>
        {val.map((item, idx) => {
          const itemParsed = parseJSONData(item);
          return (
            <span key={idx} className="badge bg-label-primary font-monospace" style={{ fontSize: "0.75rem", whiteSpace: "normal", textAlign: "left" }}>
              {typeof itemParsed === "object" ? JSON.stringify(itemParsed, null, 1) : String(item)}
            </span>
          );
        })}
      </div>
    );
  }
  if (typeof val === "object") {
    return (
      <pre className="bg-light border text-dark p-2 rounded mb-0" style={{ fontSize: "0.8rem", maxHeight: "180px", overflowY: "auto" }}>
        {JSON.stringify(val, null, 2)}
      </pre>
    );
  }
  return <span className="text-dark fw-semibold" style={{ wordBreak: "break-word" }}>{String(val)}</span>;
};

const LogDetailsModal = ({ show, log, onClose }) => {
  const [activeTab, setActiveTab] = useState("overview"); // overview, payload, previous
  const [copiedKey, setCopiedKey] = useState("");

  if (!show || !log) return null;

  const payloadData = parseJSONData(log.payload);
  const prevData = parseJSONData(log.previous_value);

  const isUpdate = !!prevData && !!payloadData && typeof prevData === "object" && typeof payloadData === "object";
  const isCreate = !prevData && !!payloadData;

  const handleCopy = (text, keyName) => {
    if (!text) return;
    const str = typeof text === "string" ? text : JSON.stringify(text, null, 2);
    navigator.clipboard.writeText(str);
    setCopiedKey(keyName);
    setTimeout(() => setCopiedKey(""), 2000);
  };

  // Compute diffs for updates
  const computeDiffs = () => {
    if (!isUpdate) return [];
    const allKeys = Array.from(new Set([...Object.keys(prevData), ...Object.keys(payloadData)]));
    const diffs = [];

    allKeys.forEach((key) => {
      if (["password", "remember_token", "_token"].includes(key)) return;
      const oldVal = prevData[key];
      const newVal = payloadData[key];

      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        diffs.push({ key, oldVal, newVal });
      }
    });

    return diffs;
  };

  const diffs = computeDiffs();

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1055 }} tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content shadow-lg border-0 bg-white">
          {/* Complete White Modal Header */}
          <div className="modal-header bg-white text-dark border-bottom py-3">
            <div>
              <h5 className="modal-title text-dark fw-bold mb-1 d-flex align-items-center gap-2">
                <i className="ri-shield-keyhole-line text-primary"></i>
                Log Record #{log.id} Details
              </h5>
              <small className="text-muted">
                Action: <span className="badge bg-label-primary text-uppercase me-2">{log.function || "N/A"}</span>
                Status:{" "}
                <span className={`badge ${log.status === "success" ? "bg-label-success" : "bg-label-danger"}`}>
                  {log.status ? log.status.toUpperCase() : "N/A"}
                </span>
              </small>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          {/* User & Request Info Banner */}
          <div className="bg-white px-4 py-3 border-bottom">
            <div className="row g-2 text-dark small">
              <div className="col-md-4">
                <i className="ri-user-3-line me-1 text-primary"></i>
                <strong>User:</strong> {log.user?.name || "System/Guest"}
              </div>
              <div className="col-md-4">
                <i className="ri-mail-line me-1 text-primary"></i>
                <strong>Email:</strong> {log.user?.email || "N/A"}
              </div>
              <div className="col-md-4">
                <i className="ri-time-line me-1 text-primary"></i>
                <strong>Date:</strong> {formatDateTime(log.log_date)}
              </div>
              <div className="col-md-8 text-truncate">
                <i className="ri-link me-1 text-primary"></i>
                <strong>URL:</strong> <code className="text-dark bg-light px-2 py-1 border rounded">{log.url || "—"}</code>
              </div>
              <div className="col-md-4">
                <i className="ri-computer-line me-1 text-primary"></i>
                <strong>IP / OS:</strong> {log.user_ip || "—"} ({log.user_operatingsystem || "N/A"})
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="px-4 pt-3 border-bottom bg-white">
            <ul className="nav nav-tabs border-0">
              <li className="nav-item">
                <button
                  className={`nav-link border-0 fw-semibold ${activeTab === "overview" ? "active border-bottom border-primary border-2 text-primary bg-white" : "text-secondary bg-white"}`}
                  onClick={() => setActiveTab("overview")}
                >
                  <i className="ri-eye-line me-1"></i> Visual Overview &amp; Diffs
                  {isUpdate && diffs.length > 0 && (
                    <span className="badge bg-warning text-dark ms-2">{diffs.length} Changed</span>
                  )}
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link border-0 fw-semibold ${activeTab === "payload" ? "active border-bottom border-primary border-2 text-primary bg-white" : "text-secondary bg-white"}`}
                  onClick={() => setActiveTab("payload")}
                >
                  <i className="ri-code-s-slash-line me-1"></i> Submitted Payload (JSON)
                </button>
              </li>
              {prevData && (
                <li className="nav-item">
                  <button
                    className={`nav-link border-0 fw-semibold ${activeTab === "previous" ? "active border-bottom border-primary border-2 text-primary bg-white" : "text-secondary bg-white"}`}
                    onClick={() => setActiveTab("previous")}
                  >
                    <i className="ri-history-line me-1"></i> Previous Value (JSON)
                  </button>
                </li>
              )}
            </ul>
          </div>

          {/* Body Content */}
          <div className="modal-body p-4 bg-white" style={{ minHeight: "350px" }}>
            {/* OVERVIEW TAB */}
            {activeTab === "overview" && (
              <div>
                {/* Status message */}
                {log.message && (
                  <div className="alert alert-light border py-2 px-3 mb-4 d-flex align-items-center justify-content-between text-dark">
                    <span>
                      <i className="ri-information-line me-2 text-primary"></i>
                      {log.message}
                    </span>
                  </div>
                )}

                {/* UPDATE ACTION: CHANGED FIELDS DIFF TABLE */}
                {isUpdate && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h6 className="fw-bold mb-0 text-dark">
                        <i className="ri-git-commit-line text-warning me-2"></i>
                        Field Changes (Diff Analysis)
                      </h6>
                      <small className="text-muted">{diffs.length} field(s) modified</small>
                    </div>

                    {diffs.length === 0 ? (
                      <div className="p-3 bg-light border rounded text-center text-muted small">
                        No specific field differences detected between previous state and new payload.
                      </div>
                    ) : (
                      <div className="table-responsive rounded border">
                        <table className="table table-sm table-bordered mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: "25%" }}>Field Name</th>
                              <th style={{ width: "37.5%" }} className="bg-danger bg-opacity-10 text-danger">
                                <i className="ri-subtract-line me-1"></i> Previous Value (Before)
                              </th>
                              <th style={{ width: "37.5%" }} className="bg-success bg-opacity-10 text-success">
                                <i className="ri-add-line me-1"></i> Updated Value (After)
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {diffs.map(({ key, oldVal, newVal }) => {
                              // Array Diff handling for permissions or lists
                              if (Array.isArray(oldVal) && Array.isArray(newVal)) {
                                const added = newVal.filter((x) => !oldVal.includes(x));
                                const removed = oldVal.filter((x) => !newVal.includes(x));
                                return (
                                  <tr key={key}>
                                    <td className="fw-bold text-dark">{key}</td>
                                    <td className="bg-danger bg-opacity-10 align-top">
                                      {removed.length > 0 ? (
                                        <div>
                                          <small className="text-danger fw-bold d-block mb-1">
                                            Removed ({removed.length}):
                                          </small>
                                          <div className="d-flex flex-wrap gap-1" style={{ maxHeight: "120px", overflowY: "auto" }}>
                                            {removed.map((r, i) => (
                                              <span key={i} className="badge bg-danger">
                                                - {String(r)}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      ) : (
                                        <em className="text-muted small">No items removed</em>
                                      )}
                                    </td>
                                    <td className="bg-success bg-opacity-10 align-top">
                                      {added.length > 0 ? (
                                        <div>
                                          <small className="text-success fw-bold d-block mb-1">
                                            Added ({added.length}):
                                          </small>
                                          <div className="d-flex flex-wrap gap-1" style={{ maxHeight: "120px", overflowY: "auto" }}>
                                            {added.map((a, i) => (
                                              <span key={i} className="badge bg-success">
                                                + {String(a)}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      ) : (
                                        <em className="text-muted small">No items added</em>
                                      )}
                                    </td>
                                  </tr>
                                );
                              }

                              return (
                                <tr key={key}>
                                  <td className="fw-bold text-dark">{key}</td>
                                  <td className="bg-danger bg-opacity-10 text-decoration-line-through text-danger">
                                    {formatValue(oldVal)}
                                  </td>
                                  <td className="bg-success bg-opacity-10 text-success fw-semibold">
                                    {formatValue(newVal)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* CREATE OR GENERAL: STRUCTURED ATTRIBUTES LIST */}
                <div>
                  <h6 className="fw-bold mb-3 text-dark">
                    <i className="ri-list-settings-line text-primary me-2"></i>
                    {isCreate ? "Submitted Record Attributes" : "Payload Data Breakdown"}
                  </h6>
                  {payloadData && typeof payloadData === "object" ? (
                    <div className="table-responsive rounded border bg-white">
                      <table className="table table-sm table-striped mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: "30%" }}>Attribute</th>
                            <th style={{ width: "70%" }}>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(payloadData).map(([k, v]) => (
                            <tr key={k}>
                              <td className="fw-semibold text-dark font-monospace" style={{ fontSize: "0.875rem" }}>{k}</td>
                              <td>{formatValue(v)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="p-3 bg-light border rounded text-muted">
                      {payloadData ? String(payloadData) : "No payload data recorded."}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* SUBMITTED PAYLOAD JSON TAB */}
            {activeTab === "payload" && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small text-muted">Formatted JSON string of submitted payload</span>
                  <div>
                    <button
                      className="btn btn-xs btn-outline-primary"
                      onClick={() => handleCopy(payloadData, "payload")}
                    >
                      <i className={copiedKey === "payload" ? "ri-check-line text-success me-1" : "ri-file-copy-line me-1"}></i>
                      {copiedKey === "payload" ? "Copied!" : "Copy JSON"}
                    </button>
                  </div>
                </div>
                <pre
                  className="p-3 rounded border text-dark"
                  style={{
                    backgroundColor: "#f8f9fa",
                    color: "#212529",
                    maxHeight: "450px",
                    overflowY: "auto",
                    fontSize: "0.85rem",
                    fontFamily: "Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace",
                    border: "1px solid #dee2e6",
                  }}
                >
                  {JSON.stringify(payloadData, null, 2) || "No payload data"}
                </pre>
              </div>
            )}

            {/* PREVIOUS VALUE JSON TAB */}
            {activeTab === "previous" && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small text-muted">Formatted JSON string of record state before update</span>
                  <button
                    className="btn btn-xs btn-outline-primary"
                    onClick={() => handleCopy(prevData, "previous")}
                  >
                    <i className={copiedKey === "previous" ? "ri-check-line text-success me-1" : "ri-file-copy-line me-1"}></i>
                    {copiedKey === "previous" ? "Copied!" : "Copy JSON"}
                  </button>
                </div>
                <pre
                  className="p-3 rounded border text-dark"
                  style={{
                    backgroundColor: "#f8f9fa",
                    color: "#212529",
                    maxHeight: "450px",
                    overflowY: "auto",
                    fontSize: "0.85rem",
                    fontFamily: "Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace",
                    border: "1px solid #dee2e6",
                  }}
                >
                  {JSON.stringify(prevData, null, 2) || "No previous value data"}
                </pre>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer bg-white border-top py-2">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
              Close Window
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogDetailsModal;
